import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  FaStar,
  FaStarHalfAlt,
  FaComments,
  FaEdit,
  FaSearch,
  FaFileExcel,
  FaFilter,
  FaCalendarAlt,
  FaReply,
  FaWhatsapp,
} from "react-icons/fa";
import * as XLSX from "xlsx";

const PRIMARY = "#6b8e23";
const PRIMARY_LIGHT = "#8fbc8f";
const PRIMARY_DARK = "#556b2f";
const PAGE_SIZE = 50;

const AdminFeedBack = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [daysBack, setDaysBack] = useState(90);

  // Filter states
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [commentFilter, setCommentFilter] = useState("all");
  const [hubFilter, setHubFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });

  // Reply modal state
  const [replyModal, setReplyModal] = useState({ open: false, order: null });
  const [replyMessage, setReplyMessage] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyStatus, setReplyStatus] = useState("");

  // Debounce search: only filter after user stops typing
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, ratingFilter, commentFilter, hubFilter, dateFilter]);

  useEffect(() => {
    fetchOrders();
  }, [daysBack]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      // Fetch all records at once — filtering is done client-side so results
      // are not limited to a single page when filters are applied
      const response = await axios.get(
        `https://dailydish.in/api/admin/feedback-orders?limit=10000&daysBack=${daysBack}`,
      );
      if (response.data.success) {
        setOrders(response.data.order || []);
      } else {
        setOrders([]);
      }
    } catch (err) {
      setError("Failed to fetch orders");
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  // Unique hub names derived once from orders
  const uniqueHubNames = useMemo(() => {
    const seen = new Set();
    const names = [];
    for (const o of orders) {
      if (o.hubName && !seen.has(o.hubName)) {
        seen.add(o.hubName);
        names.push(o.hubName);
      }
    }
    return names.sort((a, b) => a.localeCompare(b));
  }, [orders]);

  // All filters applied across the entire dataset — not per-page
  const filteredOrders = useMemo(() => {
    let result = orders;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (o) =>
          o.username?.toLowerCase().includes(lower) ||
          o.orderid?.toLowerCase().includes(lower) ||
          o.Mobilenumber?.toString().includes(lower) ||
          o.hubName?.toLowerCase().includes(lower) ||
          o.ratings?.order?.comment?.toLowerCase().includes(lower) ||
          o.ratings?.delivery?.comment?.toLowerCase().includes(lower),
      );
    }

    if (hubFilter !== "all") {
      result = result.filter((o) => o.hubName === hubFilter);
    }

    if (ratingFilter !== "all") {
      result = result.filter((o) => {
        const or = o.ratings?.order?.rating || 0;
        const dr = o.ratings?.delivery?.rating || 0;
        switch (ratingFilter) {
          case "4+":   return or >= 4 || dr >= 4;
          case "3":    return or === 3 || dr === 3;
          case "1-2":  return (or > 0 && or <= 2) || (dr > 0 && dr <= 2);
          default:     return true;
        }
      });
    }

    if (commentFilter !== "all") {
      result = result.filter((o) => {
        const hasOrder = !!o.ratings?.order?.comment;
        const hasDel   = !!o.ratings?.delivery?.comment;
        switch (commentFilter) {
          case "with-comments":     return hasOrder || hasDel;
          case "order-comments":    return hasOrder;
          case "delivery-comments": return hasDel;
          case "no-comments":       return !hasOrder && !hasDel;
          default:                  return true;
        }
      });
    }

    if (dateFilter.startDate || dateFilter.endDate) {
      const start = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
      const end   = dateFilter.endDate   ? new Date(dateFilter.endDate)   : null;
      if (end) end.setHours(23, 59, 59, 999);
      result = result.filter((o) => {
        const d = new Date(o.createdAt);
        if (start && end) return d >= start && d <= end;
        if (start) return d >= start;
        if (end)   return d <= end;
        return true;
      });
    }

    return result;
  }, [orders, searchTerm, hubFilter, ratingFilter, commentFilter, dateFilter]);

  // Client-side pagination over the filtered result
  const totalPages   = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const safePage     = Math.min(currentPage, totalPages);
  const currentOrders = filteredOrders.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  // KPI stats across all loaded orders (not filtered, so they stay stable while filtering)
  const stats = useMemo(() => ({
    total:        orders.length,
    withComments: orders.filter((o) => o.ratings?.order?.comment || o.ratings?.delivery?.comment).length,
    highRating:   orders.filter((o) => o.ratings?.order?.rating >= 4 || o.ratings?.delivery?.rating >= 4).length,
    lowRating:    orders.filter(
      (o) =>
        (o.ratings?.order?.rating > 0 && o.ratings?.order?.rating <= 2) ||
        (o.ratings?.delivery?.rating > 0 && o.ratings?.delivery?.rating <= 2),
    ).length,
  }), [orders]);

  // Top 5 hubs by feedback count
  const hubWiseStats = useMemo(() => {
    const map = {};
    for (const o of orders) {
      const hub = o.hubName || "Unknown";
      if (!map[hub]) map[hub] = { count: 0, withComments: 0, highRating: 0, lowRating: 0 };
      map[hub].count++;
      if (o.ratings?.order?.comment || o.ratings?.delivery?.comment) map[hub].withComments++;
      if (o.ratings?.order?.rating >= 4 || o.ratings?.delivery?.rating >= 4) map[hub].highRating++;
      if (
        (o.ratings?.order?.rating > 0 && o.ratings?.order?.rating <= 2) ||
        (o.ratings?.delivery?.rating > 0 && o.ratings?.delivery?.rating <= 2)
      ) map[hub].lowRating++;
    }
    return Object.entries(map)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5);
  }, [orders]);

  const clearAllFilters = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
    setRatingFilter("all");
    setCommentFilter("all");
    setHubFilter("all");
    setDateFilter({ startDate: "", endDate: "" });
  }, []);

  const applyQuickDateFilter = useCallback((range) => {
    const today = new Date();
    const pad   = (d) => d.toISOString().split("T")[0];
    const shift = (days) => { const d = new Date(today); d.setDate(d.getDate() - days); return d; };
    switch (range) {
      case "today":
        setDateFilter({ startDate: pad(today), endDate: pad(today) });
        break;
      case "yesterday": {
        const y = shift(1);
        setDateFilter({ startDate: pad(y), endDate: pad(y) });
        break;
      }
      case "last7days":
        setDateFilter({ startDate: pad(shift(7)), endDate: pad(today) });
        break;
      case "last30days":
        setDateFilter({ startDate: pad(shift(30)), endDate: pad(today) });
        break;
      case "thismonth": {
        const m = new Date(today.getFullYear(), today.getMonth(), 1);
        setDateFilter({ startDate: pad(m), endDate: pad(today) });
        break;
      }
      default:
        setDateFilter({ startDate: "", endDate: "" });
    }
  }, []);

  const exportToExcel = useCallback((rows, suffix = "") => {
    const data = rows.map((o) => ({
      "Order ID":            o.orderid || "",
      "Customer Name":       o.username || "",
      "Hub Name":            o.hubName || "",
      "Mobile Number":       o.Mobilenumber || "",
      "Food Rating":         o.ratings?.order?.rating || (o.ratings?.order?.status === "skipped" ? "Skipped" : "Not Rated"),
      "Food Comment":        o.ratings?.order?.comment || "",
      "Delivery Rating":     o.ratings?.delivery?.rating || (o.ratings?.delivery?.status === "skipped" ? "Skipped" : "Not Rated"),
      "Delivery Comment":    o.ratings?.delivery?.comment || "",
      "Date":                o.createdAt ? new Date(o.createdAt).toLocaleDateString() : "",
      "Time":                o.createdAt ? new Date(o.createdAt).toLocaleTimeString() : "",
      "Has Food Comment":    o.ratings?.order?.comment ? "Yes" : "No",
      "Has Delivery Comment":o.ratings?.delivery?.comment ? "Yes" : "No",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [
      { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 12 },
      { wch: 40 }, { wch: 15 }, { wch: 40 }, { wch: 12 }, { wch: 12 },
      { wch: 15 }, { wch: 15 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Feedback Data");
    XLSX.writeFile(wb, `Customer_Feedback${suffix}_${new Date().toISOString().split("T")[0]}.xlsx`);
  }, []);

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <FaStar key={i} color={i < rating ? "#ffc107" : "#e4e5e9"} style={{ marginRight: 2 }} />
    ));

  const openReplyModal = useCallback((order) => {
    setReplyModal({ open: true, order });
    setReplyMessage(order.ratings?.adminReply?.message || "");
    setReplyStatus("");
  }, []);

  const closeReplyModal = useCallback(() => {
    setReplyModal({ open: false, order: null });
    setReplyMessage("");
    setReplyStatus("");
  }, []);

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    setReplyLoading(true);
    setReplyStatus("");
    try {
      const res = await axios.put(
        `https://dailydish.in/api/admin/feedback-reply/${replyModal.order._id}`,
        { message: replyMessage.trim() },
      );
      if (res.data.success) {
        setReplyStatus(res.data.message);
        setOrders((prev) =>
          prev.map((o) =>
            o._id === replyModal.order._id
              ? { ...o, ratings: { ...o.ratings, adminReply: res.data.adminReply } }
              : o,
          ),
        );
      } else {
        setReplyStatus("Failed to send reply.");
      }
    } catch (err) {
      setReplyStatus("Error: " + (err.response?.data?.message || err.message));
    } finally {
      setReplyLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border" style={{ color: PRIMARY }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger text-center m-4">{error}</div>;
  }

  const todayStr = new Date().toISOString().split("T")[0];
  const hasActiveFilters =
    searchInput || hubFilter !== "all" || ratingFilter !== "all" ||
    commentFilter !== "all" || dateFilter.startDate || dateFilter.endDate;

  // Build pagination page numbers with ellipsis for large page counts
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            {/* Header */}
            <div
              className="card-header text-white d-flex justify-content-between align-items-center"
              style={{ backgroundColor: PRIMARY }}
            >
              <div>
                <h4 className="mb-0">
                  <FaComments className="me-2" />
                  Customer Feedback Dashboard
                  <span className="badge bg-light text-dark ms-2">
                    {filteredOrders.length} of {orders.length}
                  </span>
                  {hubFilter !== "all" && (
                    <span className="badge bg-info ms-2">Hub: {hubFilter}</span>
                  )}
                </h4>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-light btn-sm d-flex align-items-center" onClick={clearAllFilters}>
                  <FaFilter className="me-2" />Clear Filters
                </button>
                <button
                  className="btn btn-success btn-sm d-flex align-items-center"
                  onClick={() => exportToExcel(filteredOrders, "_filtered")}
                  disabled={filteredOrders.length === 0}
                >
                  <FaFileExcel className="me-2" />
                  Export Filtered ({filteredOrders.length})
                </button>
                <button
                  className="btn btn-outline-light btn-sm d-flex align-items-center"
                  onClick={() => exportToExcel(orders, "_all")}
                  disabled={orders.length === 0}
                >
                  <FaFileExcel className="me-2" />
                  Export All ({orders.length})
                </button>
              </div>
            </div>

            <div className="card-body">
              {/* KPI Cards */}
              <div className="row mb-4">
                {[
                  { val: stats.total,        label: "Total Feedback",    bg: PRIMARY,       Icon: FaComments },
                  { val: stats.withComments, label: "With Comments",     bg: PRIMARY_LIGHT, Icon: FaEdit },
                  { val: stats.highRating,   label: "High Ratings (4+)", bg: "#198754",     Icon: FaStar },
                  { val: stats.lowRating,    label: "Low Ratings (1-2)", bg: "#dc3545",     Icon: FaStarHalfAlt },
                ].map(({ val, label, bg, Icon }) => (
                  <div key={label} className="col-xl-3 col-md-6 mb-3">
                    <div className="card text-white" style={{ backgroundColor: bg }}>
                      <div className="card-body d-flex justify-content-between">
                        <div>
                          <h4 className="mb-0">{val}</h4>
                          <small>{label}</small>
                        </div>
                        <Icon size={24} className="opacity-75" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Hub Quick Stats */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header bg-light">
                      <h6 className="mb-0"><FaFilter className="me-2" />Hub-wise Statistics (Top 5)</h6>
                    </div>
                    <div className="card-body p-2">
                      <div className="d-flex flex-wrap gap-2">
                        {hubWiseStats.map(([hub, s]) => (
                          <div
                            key={hub}
                            className={`badge p-2 d-flex align-items-center ${hubFilter === hub ? "bg-primary" : "bg-secondary"}`}
                            style={{ cursor: "pointer" }}
                            onClick={() => setHubFilter(hubFilter === hub ? "all" : hub)}
                            title={`Click to filter by ${hub}`}
                          >
                            <span className="fw-bold">{hub}</span>
                            <span className="ms-2">{s.count}</span>
                          </div>
                        ))}
                        {uniqueHubNames.length > 5 && (
                          <span className="badge bg-light text-dark p-2">
                            +{uniqueHubNames.length - 5} more hubs
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Date Filters */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header bg-light py-2">
                      <h6 className="mb-0"><FaCalendarAlt className="me-2" />Quick Date Filters</h6>
                    </div>
                    <div className="card-body p-2">
                      <div className="d-flex flex-wrap gap-2">
                        {[
                          ["today",      "Today"],
                          ["yesterday",  "Yesterday"],
                          ["last7days",  "Last 7 Days"],
                          ["last30days", "Last 30 Days"],
                          ["thismonth",  "This Month"],
                        ].map(([key, label]) => (
                          <button
                            key={key}
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => applyQuickDateFilter(key)}
                          >
                            {label}
                          </button>
                        ))}
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setDateFilter({ startDate: "", endDate: "" })}
                        >
                          Clear Dates
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters Row */}
              <div className="row mb-4">
                <div className="col-lg-2 col-md-6 mb-2">
                  <label className="form-label small fw-bold mb-1">Date Range</label>
                  <select
                    className="form-select"
                    value={daysBack}
                    onChange={(e) => setDaysBack(Number(e.target.value))}
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={180}>Last 180 days</option>
                    <option value={365}>Last 1 year</option>
                    <option value={3650}>All time</option>
                  </select>
                </div>
                <div className="col-lg-2 col-md-6 mb-2">
                  <div className="input-group mt-4">
                    <span className="input-group-text"><FaSearch /></span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-lg-2 col-md-6 mb-2">
                  <select
                    className="form-select"
                    value={hubFilter}
                    onChange={(e) => setHubFilter(e.target.value)}
                  >
                    <option value="all">All Hubs</option>
                    {uniqueHubNames.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-2 col-md-6 mb-2">
                  <select
                    className="form-select"
                    value={ratingFilter}
                    onChange={(e) => setRatingFilter(e.target.value)}
                  >
                    <option value="all">All Ratings</option>
                    <option value="4+">High (4-5 Stars)</option>
                    <option value="3">Average (3 Stars)</option>
                    <option value="1-2">Low (1-2 Stars)</option>
                  </select>
                </div>
                <div className="col-lg-2 col-md-6 mb-2">
                  <select
                    className="form-select"
                    value={commentFilter}
                    onChange={(e) => setCommentFilter(e.target.value)}
                  >
                    <option value="all">All Comments</option>
                    <option value="with-comments">Has Comments</option>
                    <option value="order-comments">Food Comments Only</option>
                    <option value="delivery-comments">Delivery Comments Only</option>
                    <option value="no-comments">No Comments</option>
                  </select>
                </div>
                <div className="col-lg-2 col-md-6 mb-2">
                  <input
                    type="date"
                    className="form-control"
                    value={dateFilter.startDate}
                    onChange={(e) => setDateFilter((p) => ({ ...p, startDate: e.target.value }))}
                    max={dateFilter.endDate || todayStr}
                    placeholder="From Date"
                  />
                </div>
                <div className="col-lg-2 col-md-6 mb-2">
                  <input
                    type="date"
                    className="form-control"
                    value={dateFilter.endDate}
                    onChange={(e) => setDateFilter((p) => ({ ...p, endDate: e.target.value }))}
                    min={dateFilter.startDate}
                    max={todayStr}
                    placeholder="To Date"
                  />
                </div>
              </div>

              {/* Active Filters Banner */}
              {hasActiveFilters && (
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="alert alert-info py-2">
                      <small className="d-flex flex-wrap align-items-center gap-2">
                        <FaFilter />
                        Active Filters:
                        {searchInput && <span className="badge bg-secondary">Search: "{searchInput}"</span>}
                        {hubFilter !== "all" && <span className="badge bg-primary">Hub: {hubFilter}</span>}
                        {ratingFilter !== "all" && <span className="badge bg-warning text-dark">Rating: {ratingFilter}</span>}
                        {commentFilter !== "all" && <span className="badge bg-success">{commentFilter.replace(/-/g, " ")}</span>}
                        {(dateFilter.startDate || dateFilter.endDate) && (
                          <span className="badge bg-info">
                            <FaCalendarAlt className="me-1" />
                            {dateFilter.startDate || "Start"} to {dateFilter.endDate || "End"}
                          </span>
                        )}
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {/* Table */}
              {filteredOrders.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <h5>No Feedback Found</h5>
                  <p className="mb-0">Try changing your filter criteria</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-striped align-middle">
                    <thead className="text-white" style={{ backgroundColor: PRIMARY_DARK }}>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Hub Name</th>
                        <th>Food Rating</th>
                        <th>Delivery Rating</th>
                        <th>Food Comment</th>
                        <th>Delivery Comment</th>
                        <th>Food Ordered</th>
                        <th>Date</th>
                        <th>Admin Reply</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map((order) => (
                        <tr key={order._id}>
                          <td><strong>{order.orderid}</strong></td>
                          <td>
                            <div className="fw-bold">{order.username}</div>
                            <small className="text-muted">{order.Mobilenumber}</small>
                          </td>
                          <td>
                            <span className="badge bg-info">{order.hubName || "Unknown"}</span>
                          </td>
                          <td style={{ minWidth: 140 }}>
                            {order.ratings?.order?.status === "skipped" ? (
                              <span className="badge bg-secondary">Skipped</span>
                            ) : order.ratings?.order?.rating ? (
                              <div className="text-warning">{renderStars(order.ratings.order.rating)}</div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td style={{ minWidth: 140 }}>
                            {order.ratings?.delivery?.status === "skipped" ? (
                              <span className="badge bg-secondary">Skipped</span>
                            ) : order.ratings?.delivery?.rating ? (
                              <div className="text-warning">{renderStars(order.ratings.delivery.rating)}</div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            {order.ratings?.order?.comment
                              ? <small>{order.ratings.order.comment}</small>
                              : <span className="text-muted">-</span>}
                          </td>
                          <td>
                            {order.ratings?.delivery?.comment
                              ? <small>{order.ratings.delivery.comment}</small>
                              : <span className="text-muted">-</span>}
                          </td>
                          <td>
                            <div className="small">
                              {order?.allProduct?.map((item, idx) => (
                                <div key={idx} className="mb-1">
                                  {item?.name || item?.foodItemId?.foodname || "—"} ×{item?.quantity}
                                </div>
                              ))}
                            </div>
                          </td>
                          <td>
                            <small>{new Date(order.createdAt).toLocaleDateString()}</small>
                            <br />
                            <small className="text-muted">{new Date(order.createdAt).toLocaleTimeString()}</small>
                          </td>
                          <td style={{ minWidth: 160 }}>
                            {order.ratings?.adminReply?.message ? (
                              <div>
                                <small className="text-success d-block">
                                  <FaWhatsapp className="me-1" />
                                  {order.ratings.adminReply.sentViaWhatsapp ? "Sent via WhatsApp" : "Saved (WA failed)"}
                                </small>
                                <small className="text-muted d-block" style={{ maxWidth: 180, whiteSpace: "pre-wrap" }}>
                                  {order.ratings.adminReply.message}
                                </small>
                                <button className="btn btn-outline-secondary btn-sm mt-1" onClick={() => openReplyModal(order)}>
                                  <FaEdit className="me-1" />Edit
                                </button>
                              </div>
                            ) : (
                              <button
                                className="btn btn-sm text-white"
                                style={{ backgroundColor: PRIMARY }}
                                onClick={() => openReplyModal(order)}
                              >
                                <FaReply className="me-1" />Reply
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">
                    Page {safePage} of {totalPages} · Showing{" "}
                    {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredOrders.length)}{" "}
                    of {filteredOrders.length} results
                  </small>
                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setCurrentPage((p) => p - 1)}>‹</button>
                      </li>
                      {pageNumbers.map((p, idx) =>
                        p === "..." ? (
                          <li key={`e${idx}`} className="page-item disabled">
                            <span className="page-link">…</span>
                          </li>
                        ) : (
                          <li key={p} className={`page-item ${safePage === p ? "active" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(p)}
                              style={
                                safePage === p
                                  ? { backgroundColor: PRIMARY, borderColor: PRIMARY }
                                  : { color: PRIMARY }
                              }
                            >
                              {p}
                            </button>
                          </li>
                        ),
                      )}
                      <li className={`page-item ${safePage === totalPages ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setCurrentPage((p) => p + 1)}>›</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reply Modal */}
      {replyModal.open && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeReplyModal(); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header text-white" style={{ backgroundColor: PRIMARY }}>
                <h5 className="modal-title">
                  <FaReply className="me-2" />Reply to Feedback
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={closeReplyModal} />
              </div>
              <div className="modal-body">
                <div className="mb-3 p-2 bg-light rounded small">
                  <div><strong>Customer:</strong> {replyModal.order?.username}</div>
                  <div><strong>Mobile:</strong> {replyModal.order?.Mobilenumber}</div>
                  <div><strong>Order ID:</strong> {replyModal.order?.orderid}</div>
                  {replyModal.order?.ratings?.order?.comment && (
                    <div className="mt-1">
                      <strong>Food Comment:</strong> {replyModal.order.ratings.order.comment}
                    </div>
                  )}
                  {replyModal.order?.ratings?.delivery?.comment && (
                    <div className="mt-1">
                      <strong>Delivery Comment:</strong> {replyModal.order.ratings.delivery.comment}
                    </div>
                  )}
                </div>
                <div className="mb-3 p-2 border rounded small text-muted">
                  <FaWhatsapp className="me-1 text-success" />
                  <strong>WhatsApp message preview (Feedback_Response):</strong>
                  <div className="mt-1 text-black" style={{ whiteSpace: "pre-line" }}>
                    {`Hi ${replyModal.order?.username || "Customer"},\n\nWe're sorry to hear about your experience with your order ${replyModal.order?.orderid}.\n\nHere's what happened:\n${replyMessage || "<your message>"}\n\nThanks,\nDailyDish Team`}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Your Reply Message <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    placeholder="Type your reply to the customer..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                  />
                  <small className="text-muted">
                    This will be sent as <strong>&#123;&#123;3&#125;&#125;</strong> in the WhatsApp template.
                  </small>
                </div>
                {replyStatus && (
                  <div
                    className={`alert py-2 ${
                      replyStatus.includes("Error") || replyStatus.includes("Failed")
                        ? "alert-danger"
                        : "alert-success"
                    }`}
                  >
                    <small>{replyStatus}</small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeReplyModal} disabled={replyLoading}>
                  Cancel
                </button>
                <button
                  className="btn text-white d-flex align-items-center gap-2"
                  style={{ backgroundColor: PRIMARY }}
                  onClick={handleSendReply}
                  disabled={replyLoading || !replyMessage.trim()}
                >
                  {replyLoading ? (
                    <><span className="spinner-border spinner-border-sm" />Sending...</>
                  ) : (
                    <><FaWhatsapp />Send via WhatsApp</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFeedBack;
